import { useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Icon,
  Stack,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const CsvUploadButtonOrder = ({ buttonStyle = '' }) => {
  const [tab, setTab] = useState(0);

  const [csvFile, setCsvFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const downloadBlob = (data, fileName) => {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);

    const blob = new Blob([bom, data], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);

    link.click();

    link.remove();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await axiosInstance.post(
        '/order/csv-template',
        {},
        {
          responseType: 'blob',
        }
      );

      downloadBlob(
        res.data,
        'tendenec-order-csv-template.csv'
      );
    } catch (err) {
      console.error('テンプレートダウンロード失敗:', err);

      setMessage(
        'エラー: テンプレートCSVのダウンロードに失敗しました'
      );
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await axiosInstance.post(
        '/order/download-csv',
        {
          from,
          to,
        },
        {
          responseType: 'blob',
        }
      );

      downloadBlob(
        res.data,
        `tendenec-order-${from || 'from'}-${to || 'to'}.csv`
      );
    } catch (err) {
      console.error('CSVダウンロード失敗:', err);

      setMessage(
        'エラー: 注文CSVのダウンロードに失敗しました'
      );
    }
  };

  const handleFileChange = (e) => {
    setCsvFile(e.target.files[0]);
    setMessage('');
  };

  const handleUpload = async () => {
    if (!csvFile) {
      setMessage('CSVファイルを選択してください。');
      return;
    }

    const formData = new FormData();

    formData.append('csv', csvFile);

    try {
      setIsUploading(true);

      const response = await axiosInstance.post(
        '/order/upload-csv',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setMessage(
        response.data.message || 'アップロード成功'
      );
    } catch (error) {
      console.error('CSVアップロード失敗:', error);

      const errMsg =
        error.response?.data?.message ??
        error.message ??
        'アップロードに失敗しました';

      setMessage(`エラー: ${errMsg}`);
    } finally {
      setIsUploading(false);
      setCsvFile(null);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setMessage('');
    setCsvFile(null);
    setFrom('');
    setTo('');
    setTab(0);
  };

  return (
    <>
      <Button
        variant="related-menu"
        onClick={() => setIsModalOpen(true)}
        className={buttonStyle}
      >
        <UploadFileIcon />
        注文CSV登録
      </Button>

      <Dialog
        open={isModalOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          注文CSV
        </DialogTitle>

        <Tabs
          value={tab}
          onChange={(e, value) => setTab(value)}
        >
          <Tab label="アップロード" />
          <Tab label="ダウンロード" />
        </Tabs>

        <DialogContent>

          {tab === 0 && (
            <Stack spacing={3} mt={2}>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                CSVファイルをアップロードして注文を一括登録します。
              </Typography>

              <Button
                onClick={handleDownloadTemplate}
                startIcon={<DownloadIcon />}
                variant="text"
                sx={{
                  width: 'fit-content',
                  p: 0,
                  minWidth: 0,
                  textDecoration: 'underline',
                }}
              >
                テンプレートCSVをダウンロード
              </Button>

              <input
                id="csv-upload-order"
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />

              <Stack direction="row" gap={1}>
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor="csv-upload-order"
                >
                  CSVファイルを選択
                </Button>

                <Button
                  onClick={handleUpload}
                  variant="contained"
                  disabled={isUploading || !csvFile}
                  startIcon={<Icon>upload</Icon>}
                >
                  {isUploading
                    ? 'アップロード中…'
                    : 'CSVで登録する'}
                </Button>
              </Stack>

              {csvFile && (
                <Typography
                  variant="body2"
                >
                  選択中のファイル: {csvFile.name}
                </Typography>
              )}

            </Stack>
          )}
          {tab === 1 && (
            <Stack spacing={3} mt={2}>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                指定した期間の注文データをCSVで出力します。
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2} justifyContent='center' alignItems='center'
              >
                <TextField
                  label="開始日"
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Typography>～</Typography>
                <TextField
                  label="終了日"
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Stack>

              <Button
                onClick={handleDownloadCsv}
                startIcon={<DownloadIcon />}
                variant="contained"
                sx={{ width: 'fit-content' }}
              >
                注文CSVをダウンロード
              </Button>

            </Stack>
          )}

          {message && (
            <Typography
              variant="body2"
              sx={{ mt: 3 }}
              color={
                message.startsWith('エラー')
                  ? 'error'
                  : 'primary'
              }
            >
              {message}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CsvUploadButtonOrder;